import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { InvoiceFee } from '../../../../../core/models/invoice-settings/invoice-settings.model';
import { FeeType, MasterFee } from '../../../../../core/models/information.model';
import { MapperInvoiceSettingsService } from '../../../../../core/mappers/invoice-settings/mapper-invoice-settings.service';
import { ApiInvoiceSettingsService } from '../../../../../core/services/api/invoice-settings/api-invoice-settings.service';
import { ApiFormService } from '../../../../../core/services/api-form.service';
import { ApiService } from '../../../../../core/services/api.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-invoice-fee',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-invoice-fee.component.html',
  styleUrl: './modal-invoice-fee.component.css',
})
export class ModalInvoiceFeeComponent implements OnInit {
  @Input() data: InvoiceFee | null = null;
  @Input() siteId: number | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  formData: Partial<InvoiceFee> = this.emptyForm();
  feeMasters: MasterFee[] = [];
  feeTypes: FeeType[] = [];
  isLoading = signal(false);

  private readonly api = inject(ApiInvoiceSettingsService);
  private readonly apiForm = inject(ApiFormService);
  private readonly apiLegacy = inject(ApiService);
  private readonly mapper = inject(MapperInvoiceSettingsService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  ngOnInit(): void {
    if (this.data) {
      this.formData = { ...this.data };
    }
    this.loadLookups();
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อรายการ');
      return;
    }
    if (!this.formData.feeId) {
      this.toast.error('กรุณาเลือกรายการค่าบริการ (Fee Master)');
      return;
    }
    if (!this.formData.feeTypeId) {
      this.toast.error('กรุณาเลือกประเภทรายรับ');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const isEdit = (this.formData.id ?? 0) > 0;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const payload = this.mapper.mapFeeToPayload(this.formData, userId, menuId, this.siteId);

    this.isLoading.set(true);
    const request$ = this.formData.id
      ? this.api.updateInvoiceFee(this.formData.id, payload)
      : this.api.insertInvoiceFee(payload);

    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.close.emit();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
        this.isLoading.set(false);
      },
    });
  }

  onFeeMasterChange(): void {
    const selected = this.feeMasters.find((f) => f.id === Number(this.formData.feeId));
    if (selected && !this.formData.name) {
      this.formData.name = selected.name;
    }
  }

  private loadLookups(): void {
    this.apiLegacy.getFeeMaster().subscribe({
      next: (res) => (this.feeMasters = res ?? []),
      error: () => (this.feeMasters = []),
    });
    this.apiForm.getFeeType().subscribe({
      next: (res) => (this.feeTypes = res ?? []),
      error: () => (this.feeTypes = []),
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  private emptyForm(): Partial<InvoiceFee> {
    return {
      id: 0,
      name: '',
      feeId: 0,
      fee: 0,
      vat: true,
      feeTypeId: null,
      sortOrder: 0,
      description: '',
      status: true,
    };
  }

  get canShowActivityHistory(): boolean {
    return (this.formData.id ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
